import { useState } from "react";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCorners, DragOverlay } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { ChecklistCard } from "./ChecklistCard";

export const BoardView = ({ board, actions, readOnly = false }) => {
  const [newList, setNewList] = useState("");
  const [active, setActive] = useState(null);
  const allSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const sensors = readOnly ? [] : allSensors;

  const findList = (id) => board.checklists.find((c) => c.checklist_id === id || c.items.some((i) => i.todo_id === id) || `drop-${c.checklist_id}` === id);

  const onDragEnd = ({ active: a, over }) => {
    setActive(null);
    if (!over) return;
    if (a.data.current?.type === "checklist") {
      if (a.id === over.id) return;
      const ids = board.checklists.map((c) => c.checklist_id);
      actions.reorderChecklists(board.board_id, arrayMove(ids, ids.indexOf(a.id), ids.indexOf(over.id)));
      return;
    }
    const from = findList(a.id), to = findList(over.id);
    if (!from || !to) return;
    const item = from.items.find((i) => i.todo_id === a.id);
    const openIds = (list) => list.items.filter((i) => i.completed === item.completed).map((i) => i.todo_id);
    if (from.checklist_id === to.checklist_id) {
      const ids = openIds(from);
      const oi = ids.indexOf(over.id);
      if (oi < 0 || a.id === over.id) return;
      actions.reorderItems(from.checklist_id, arrayMove(ids, ids.indexOf(a.id), oi));
    } else {
      const ids = openIds(to).filter((id) => id !== a.id);
      const oi = ids.indexOf(over.id);
      ids.splice(oi < 0 ? ids.length : oi, 0, a.id);
      actions.reorderItems(to.checklist_id, ids, from.checklist_id);
    }
  };

  const addList = (e) => { e.preventDefault(); if (newList.trim()) { actions.addChecklist(board.board_id, newList.trim()); setNewList(""); } };
  const activeItem = active?.type === "item" ? board.checklists.flatMap((c) => c.items).find((i) => i.todo_id === active.id) : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={({ active: a }) => setActive({ id: a.id, type: a.data.current?.type })} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
      <div className="flex gap-4 overflow-x-auto pb-4 items-start min-h-[320px]" data-testid={`board-${board.board_id}`}>
        <SortableContext items={board.checklists.map((c) => c.checklist_id)} strategy={horizontalListSortingStrategy}>
          {board.checklists.map((c) => <ChecklistCard key={c.checklist_id} checklist={c} onAddItem={actions.addItem} onRename={actions.renameChecklist} onDelete={actions.deleteChecklist} onToggle={actions.toggleItem} onEdit={actions.editItem} onAddNote={actions.addNote} onEditNote={actions.editNote} onDeleteNote={actions.deleteNote} />)}
        </SortableContext>
        {!readOnly && (
          <form onSubmit={addList} className="w-[280px] shrink-0 rounded-xl border border-dashed border-slate-300 p-3 flex items-center gap-2 bg-white/60">
            <Plus className="h-4 w-4 text-slate-400" /><Input className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 px-0" placeholder="Add a checklist…" value={newList} onChange={(e) => setNewList(e.target.value)} data-testid="checklist-add-input" />
          </form>
        )}
      </div>
      <DragOverlay>{activeItem ? <div className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-lg w-[290px]">{activeItem.title}</div> : null}</DragOverlay>
    </DndContext>
  );
};
